import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/api';
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import '../styles/InventoryCleanupPage.css';
import handleImageErrorWithPasomaruFallback from '../utils/imageUtils';

const InventoryCleanupPage = () => {
  const [inventoryList, setInventoryList] = useState([]);
  const [filter, setFilter] = useState('');
  const [field, setField] = useState('manufacturer');
  const [editedItems, setEditedItems] = useState({});
  const [bulkFrom, setBulkFrom] = useState('');
  const [bulkTo, setBulkTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const pageSize = 10;
  const totalPages = Math.ceil(totalCount / pageSize);
  const [fieldList, setFieldList] = useState([]);
  const navigate = useNavigate();
  const [storeId, setStoreId] = useState("");
  const { user: userInfo } = useAuthContext();

  const fetchInventory = useCallback(async () => {
    try {
      const queryParam = `${field}=${encodeURIComponent(filter)}&page=${page}&page_size=${pageSize}`;
      const res = await apiRequest(`inventory/inventory/?${queryParam}`);
      setInventoryList(Array.isArray(res?.results) ? res.results : []);
      setHasNext(!!res.next);
      setHasPrev(!!res.previous);
      setTotalCount(res.count || 0);
    } catch (err) {
      console.error("取得エラー:", err);
      setInventoryList([]);
      setHasNext(false);
      setHasPrev(false);
    }
  }, [filter, field, page]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const fetchFieldList = useCallback(async () => {
    try {
      const res = await apiRequest(`inventory/unique_field/?field=${field}`);
      setFieldList(res || []);
    } catch (err) {
      console.error("フィールド一覧取得失敗:", err);
      setFieldList([]);
    }
  }, [field]);
  
  useEffect(() => {
    fetchFieldList();
  }, [fetchFieldList]);

  useEffect(() => {
    if (userInfo?.store_id) {
      setStoreId(userInfo.store_id);
    }
  }, [userInfo]);


  const handleChange = (id, newVal) => {
    setEditedItems(prev => ({ ...prev, [id]: newVal }));
  };

  const handleUpdate = async (id) => {
    const newValue = editedItems[id];
    if (!newValue) return;
    await apiRequest(`inventory/inventory/${id}/`, 'PATCH', { [field]: newValue });
    fetchInventory();
  };

  const handleBulkReplace = async () => {
    const affected = inventoryList.filter(item => item[field] === bulkFrom);
    for (const item of affected) {
      await apiRequest(`inventory/inventory/${item.id}/`, 'PATCH', { [field]: bulkTo });
    }
    fetchInventory();
  };


  return (
    <div className="InventoryCleanupPage-container">

      <div className="InventoryCleanupPage-columns">
        <div className="InventoryCleanupPage-left">
          <div className="InventoryCleanupPage-controls">
            <label>対象フィールド:</label>
            <select
              className="InventoryCleanupPage-select"
              value={field}
              onChange={e => {
                setField(e.target.value);
                setFilter('');
                setPage(1);
              }}
              name="InventoryCleanupPage-field"
            >
              <option value="manufacturer">メーカー</option>
              <option value="title">タイトル</option>
            </select>

            <label>フィルター:</label>
            <input
              type="text"
              value={filter}
              onChange={(e) => {
                setPage(1);
                setFilter(e.target.value);
              }}
              placeholder="語句を入力"
              name="InventoryCleanupPage-filter"
            />
          </div>

          <div className="InventoryCleanupPage-list">
            {Array.isArray(inventoryList) && inventoryList.map(item => (
              <div className="InventoryCleanupPage-row" key={item.id}>
                <div onClick={() => navigate(`/Inventory_detail/${item.id}/${storeId}`)}>ID:{item.id}</div>
                <div>
                  <img
                    src={`/media/product_images/${item.sku}.jpg?`}
                    alt="商品画像"
                    onError={(e) => handleImageErrorWithPasomaruFallback(e, item.sku)}
                  />
                </div>
                <div>現在: {item[field]}</div>
                <input
                  type="text"
                  value={editedItems[item.id] ?? item[field]}
                  onChange={(e) => handleChange(item.id, e.target.value)}
                  name={`InventoryCleanupPage-input-${item.id}`}
                />
                <button onClick={() => handleUpdate(item.id)} className="InventoryCleanupPage-button">更新</button>
              </div>
            ))}
          </div>

          <div className="InventoryCleanupPage-pagination">
            <button onClick={() => setPage(1)} disabled={page === 1}>≪ 最初</button>
            <button onClick={() => setPage(p => p - 1)} disabled={!hasPrev}>＜ 前</button>
            <span>ページ: {page} / {Math.max(1, totalPages)}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={!hasNext}>次 ＞</button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >最後 ≫</button>
          </div>

          <div className="InventoryCleanupPage-bulk">
            <h3>一括置換</h3>
            <input
              type="text"
              value={bulkFrom}
              onChange={(e) => setBulkFrom(e.target.value)}
              placeholder="元の語句"
              name="InventoryCleanupPage-bulk-from"
            />
            <input
              type="text"
              value={bulkTo}
              onChange={(e) => setBulkTo(e.target.value)}
              placeholder="置換後"
              name="InventoryCleanupPage-bulk-to"
            />
            <button onClick={handleBulkReplace} className="InventoryCleanupPage-button">一括実行</button>
          </div>
        </div>

        <div className="InventoryCleanupPage-right">
          <h3>{field === "manufacturer" ? "メーカー一覧" : "タイトル一覧"}</h3>
          <ul>
            {fieldList.map((val, idx) => (
              <li
                key={idx}
                onClick={() => {
                  setPage(1);
                  setFilter(val);
                }}
                className="InventoryCleanupPage-clickable"
              >
                {val}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InventoryCleanupPage;
